/**
 * @fileoverview The page's permanent name, and why nothing generates it from
 * the title any more.
 *
 * The `d` tag is part of the address. It is in every link anyone shares, it
 * survives every edit, and changing it does not rename a page: it abandons the
 * old one and starts a new one somewhere else.
 *
 * THAT MAKES IT THE ONE IRREVERSIBLE DECISION IN THE WHOLE DESIGN, and it is
 * the reason the title no longer feeds it. Operator ruling 2026-09-15, widening
 * an earlier ruling that applied this to private pages only.
 *
 * Everything else about a page can be withdrawn. The body can be taken down,
 * the page can be made private going forward, the blob can be deleted. The name
 * cannot be revisited at all. So a page published as "Acme Acquisition Notes"
 * carries that phrase forever, INCLUDING THROUGH a later decision to go private
 * that its author made precisely because they wished it were not public. The
 * narrower version of this rule was argued on the grounds that a public page's
 * name leaks nothing its own body does not already publish. That is true at the
 * moment of publishing and it is wrong about time, which is what matters here.
 *
 * AND A READABLE NAME IS A GUESSABLE NAME, which is an attack rather than a
 * disclosure and is the half neither of the earlier rulings named. Anyone who
 * knows an author's key can probe likely names against the relay and learn
 * which pages exist, INCLUDING PRIVATE ONES, because existence is answerable
 * without decryption. Sixteen random hex characters are not enumerable. So the
 * opaque name is not only refusing to hand over the title, it is refusing to
 * confirm the page.
 *
 * AN AUTHOR MAY STILL TYPE A READABLE NAME, and a personal site and the
 * internal runbook site both want one. That stays available as a DELIBERATE
 * ACT. What is gone is anything that proposes one, for any visibility: a
 * suggestion pointing at the irreversible option is a default wearing a
 * suggestion's clothes.
 *
 * THERE IS DELIBERATELY NO `slugFromTitle` HERE ANY MORE. It was the function
 * the editor used to fill the address from the title on every keystroke, and it
 * is not kept "in case someone wants it": an unused helper that turns a title
 * into a name is the loaded gun for exactly the behaviour this ruling removed,
 * and the next person to want a readable default would find it and wire it back
 * up. Reintroducing it means reopening the ruling, which is the intended cost.
 */

/**
 * What a `d` tag may contain.
 *
 * DEFINED HERE AND IMPORTED BY THE PUBLISHER, so the editor's inline complaint
 * and the publisher's refusal cannot drift into disagreeing about what a valid
 * name is. Two copies of this expression would eventually differ by one
 * character, and the symptom would be an editor that lets an author type a name
 * the publisher then rejects.
 */
export const IDENTIFIER_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isValidIdentifier(value: string): boolean {
  return IDENTIFIER_PATTERN.test(value);
}



/**
 * How many random bytes an opaque name carries.
 *
 * Eight, so the name is sixteen characters: short enough to read back over the
 * phone, and far beyond any chance of two of an author's own pages colliding.
 * The number is not a security parameter -- nothing is protected by guessing
 * this name, because the bytes behind it are encrypted either way. It only has
 * to be a name that says nothing.
 */
const OPAQUE_BYTES = 8;

/**
 * A name that carries no information about the page.
 *
 * Lowercase hex, so it satisfies `IDENTIFIER_PATTERN` without a second rule to
 * keep in step with it. `crypto.getRandomValues` rather than `Math.random`, not
 * because this needs to be unguessable but because a name generated from a
 * predictable sequence would repeat across two tabs opened at the same moment,
 * and two drafts sharing a name is a page silently overwriting another one.
 */
export function opaqueIdentifier(): string {
  const bytes = new Uint8Array(OPAQUE_BYTES);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The name to offer for a draft that does not have one yet.
 *
 * OPAQUE FOR EVERY PAGE, public and private alike, and it takes no arguments
 * BECAUSE THERE IS NOTHING IT COULD USE. That is the shape of the ruling rather
 * than an accident of this signature: a version of this that accepted the title
 * or the visibility would be a version that could be persuaded to derive
 * something, and the whole point is that the permanent part of a page's address
 * is never generated from anything the author might later regret.
 */
export function suggestedIdentifier(): string {
  return opaqueIdentifier();
}

/**
 * The name to offer for a COLLECTION that does not have one yet.
 *
 * TAKES THE VISIBILITY, WHICH IS THE ONE PLACE IN THIS FILE THAT DOES, and the
 * asymmetry is deliberate rather than an inconsistency with `suggestedIdentifier`
 * above.
 *
 * A page's name is opaque whatever its visibility, because a public page can be
 * made private later and its name follows it. A COLLECTION cannot make that
 * move quietly: its visibility is a property of what is in it, and a collection
 * that has even one public member is a site a stranger is meant to read, with a
 * readable address as part of the point. A personal site living at a string of
 * hex would be a worse product for no privacy gain, because its title and its
 * whole member list are already public.
 *
 * THE PRIVATE CASE IS WHERE THIS EARNS ITS KEEP, and it is the trap that was
 * nearly walked into. A collection with no public member publishes its `d` tag
 * and NOTHING else -- no title, no summary, no members. Encrypting the title
 * while generating the address from it would accomplish exactly nothing:
 * `acme-acquisition-diligence` says everything the title said, in the one field
 * that had to stay in the clear. The fix would have looked complete and the leak
 * would have survived it.
 *
 * That is the identical mistake the page side made and corrected by deleting the
 * helper that turned a title into a name. This function exists so the same
 * deletion cannot be undone here by accident: it takes no title, so there is
 * nothing for a future change to reach for.
 */
export function suggestedCollectionIdentifier(hasPublicMember: boolean): string {
  return hasPublicMember ? '' : opaqueIdentifier();
}

/**
 * The name to offer for a THEME that does not have one yet. There is none.
 *
 * A THEME IS NOT A PAGE AND THE OPAQUE RULE DOES NOT REACH IT, which is worth
 * saying in code rather than leaving to whoever notices the inconsistency next.
 * The page ruling rests on two arguments and a theme answers both differently.
 *
 * The first is that a name is the one irreversible decision, so it must not
 * carry something its author may later regret publishing. A theme's entire
 * content IS its styling, there is no private theme and there cannot be one --
 * every reader of every page using it fetches it -- so a theme named
 * `dark-serif` discloses precisely what the theme already discloses. There is
 * no later regret for the name to survive into.
 *
 * The second is that a readable name is a guessable name, and probing an
 * author's key for likely names reveals which pages EXIST, private ones
 * included. A theme's existence is not a secret at any point: it is named in
 * the public `theme` tag of every page that wears it.
 *
 * What is left is an author choosing an address they will type into pages by
 * hand and recognise later, and hex would be worse at that for no gain. So this
 * returns the empty string -- an author names their own theme -- exactly as the
 * public-collection case does, and for the same reason.
 *
 * IT TAKES NO TITLE, and that is the part to keep. Returning '' rather than
 * deriving `dark-serif` from "Dark Serif" is not caution about this artifact;
 * it is refusing to reintroduce anywhere in this file the title-to-name helper
 * whose deletion is the page ruling. The next person who wants a readable
 * default would find it here and wire it back up.
 */
export function suggestedThemeIdentifier(): string {
  return '';
}
